import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Calendar, Plus, Loader2, Check, Trash2, RotateCcw, Clock, AlertCircle
} from 'lucide-react';
import { format, parseISO, isPast, differenceInDays } from 'date-fns';

const REVIEW_TYPES = [
  'Annual Review', 'Compliance Check', 'Estate Planning Review',
  'Insurance Review', 'Tax Review', 'Investment Strategy', 'Trust Review',
  'SMSF Audit', 'Succession Planning', 'Risk Assessment'
];

const FREQUENCIES = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'semi-annual', label: 'Semi-Annual' },
  { id: 'annual', label: 'Annual' },
];

const SERVICE_LINES = [
  { id: 'family-office', name: 'Family Office' },
  { id: 'wealth', name: 'Wealth Management' },
  { id: 'tax', name: 'Tax' },
  { id: 'tax-advisory', name: 'Tax Advisory' },
  { id: 'philanthropy', name: 'Philanthropy' },
  { id: 'trustee-legal', name: 'Trustee / Estate / Legal' },
];

export default function RecurringReviews({ clientId }) {
  const { token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    reviewType: 'Annual Review',
    frequency: 'annual',
    nextReviewDate: '',
    serviceLine: '',
    notes: ''
  });

  const [completeData, setCompleteData] = useState({
    attendees: '',
    notes: '',
    outcome: 'completed'
  });

  const fetchReviews = useCallback(async () => {
    try {
      const res = await api.get(token, `/clients/${clientId}/reviews`);
      setReviews(res.data.reviews || []);
    } catch (err) {
      console.error('Reviews fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleCreate = async () => {
    if (!formData.nextReviewDate) { toast.error('Next review date is required'); return; }
    setSaving(true);
    try {
      await api.post(token, `/clients/${clientId}/reviews`, {
        ...formData,
        nextReviewDate: new Date(formData.nextReviewDate).toISOString()
      });
      toast.success('Review schedule created');
      setShowCreate(false);
      setFormData({ reviewType: 'Annual Review', frequency: 'annual', nextReviewDate: '', serviceLine: '', notes: '' });
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create review');
    }
    setSaving(false);
  };

  const handleComplete = async () => {
    if (!selectedReview) return;
    setSaving(true);
    try {
      const res = await api.post(token, `/clients/${clientId}/reviews/${selectedReview.id}/complete`, {
        attendees: completeData.attendees.split(',').map(a => a.trim()).filter(Boolean),
        notes: completeData.notes,
        outcome: completeData.outcome
      });
      toast.success(res.data.message || 'Review completed');
      setShowComplete(false);
      setCompleteData({ attendees: '', notes: '', outcome: 'completed' });
      fetchReviews();
    } catch (err) {
      toast.error('Failed to complete review');
    }
    setSaving(false);
  };

  const handleDelete = async (reviewId) => {
    try {
      await api.delete(token, `/clients/${clientId}/reviews/${reviewId}`);
      toast.success('Review schedule removed');
      fetchReviews();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const getStatusBadge = (review) => {
    if (!review.nextReviewDate) return { label: 'No Date', color: 'bg-stone-100 text-stone-600' };
    const nextDate = parseISO(review.nextReviewDate);
    const daysUntil = differenceInDays(nextDate, new Date());

    if (daysUntil < 0) return { label: `${Math.abs(daysUntil)}d overdue`, color: 'bg-red-100 text-red-700' };
    if (daysUntil <= 14) return { label: `${daysUntil}d away`, color: 'bg-amber-100 text-amber-700' };
    return { label: `${daysUntil}d away`, color: 'bg-green-100 text-green-700' };
  };

  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white" data-testid="recurring-reviews">
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#4A3B32]" />
            <h3 className="font-heading text-base text-[#2C2420]">Recurring Reviews</h3>
            {reviews.length > 0 && (
              <Badge className="bg-[#F0EBE6] text-[#4A3B32] text-[10px]">{reviews.length}</Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCreate(true)}
            className="text-xs h-8 gap-1"
            data-testid="add-review-btn"
          >
            <Plus className="w-3 h-3" />
            Schedule Review
          </Button>
        </div>
        <p className="text-xs text-[#8C8078] mt-1">Automated review scheduling with completion tracking</p>
      </div>

      <div className="px-5 pb-5">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" /></div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-6 text-sm text-[#8C8078]">
            No reviews scheduled. Create one to ensure regular client check-ins.
          </div>
        ) : (
          <div className="space-y-2 mt-2">
            {reviews.map(rev => {
              const status = getStatusBadge(rev);
              const slMeta = SERVICE_LINES.find(s => s.id === rev.serviceLine);
              return (
                <div
                  key={rev.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-[#E6E0D9] hover:border-[#A86A3A]/30 transition-all"
                  data-testid={`review-${rev.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-[#2C2420]">{rev.reviewType}</span>
                      <Badge className={`text-[10px] ${status.color}`}>
                        {isPast(parseISO(rev.nextReviewDate)) ? <AlertCircle className="w-3 h-3 mr-0.5" /> : <Clock className="w-3 h-3 mr-0.5" />}
                        {status.label}
                      </Badge>
                      {slMeta && <Badge className="text-[10px] bg-[#F0EBE6] text-[#4A3B32]">{slMeta.name}</Badge>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-[#8C8078]">
                      <span>{FREQUENCIES.find(f => f.id === rev.frequency)?.label || rev.frequency}</span>
                      <span>Next: {rev.nextReviewDate ? format(parseISO(rev.nextReviewDate), 'dd MMM yyyy') : 'Not set'}</span>
                      {rev.lastReviewDate && <span>Last: {format(parseISO(rev.lastReviewDate), 'dd MMM yyyy')}</span>}
                      {rev.history?.length > 0 && <span>{rev.history.length} completed</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => { setSelectedReview(rev); setShowComplete(true); }}
                      className="text-xs h-7 gap-1 text-green-700 border-green-200 hover:bg-green-50"
                      data-testid={`complete-review-${rev.id}`}
                    >
                      <Check className="w-3 h-3" /> Complete
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleDelete(rev.id)}
                      className="h-7 w-7 p-0 text-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Review Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="create-review-modal">
          <DialogHeader>
            <DialogTitle>Schedule Recurring Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Review Type</Label>
              <Select value={formData.reviewType} onValueChange={v => setFormData(p => ({ ...p, reviewType: v }))}>
                <SelectTrigger data-testid="review-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REVIEW_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select value={formData.frequency} onValueChange={v => setFormData(p => ({ ...p, frequency: v }))}>
                  <SelectTrigger data-testid="review-freq-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Service Line</Label>
                <Select value={formData.serviceLine} onValueChange={v => setFormData(p => ({ ...p, serviceLine: v }))}>
                  <SelectTrigger data-testid="review-sl-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_LINES.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Next Review Date</Label>
              <Input type="date" value={formData.nextReviewDate} onChange={e => setFormData(p => ({ ...p, nextReviewDate: e.target.value }))} data-testid="review-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} placeholder="Any specific agenda items or preparation needed" className="text-sm min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#4A3B32] text-white hover:bg-[#3a2e26]" data-testid="save-review-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Review Dialog */}
      <Dialog open={showComplete} onOpenChange={setShowComplete}>
        <DialogContent className="sm:max-w-md" data-testid="complete-review-modal">
          <DialogHeader>
            <DialogTitle>Complete Review: {selectedReview?.reviewType}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Attendees <span className="text-xs text-[#8C8078]">(comma-separated)</span></Label>
              <Input value={completeData.attendees} onChange={e => setCompleteData(p => ({ ...p, attendees: e.target.value }))} placeholder="James Quinn, Sarah Mitchell" data-testid="review-attendees" />
            </div>
            <div className="space-y-2">
              <Label>Outcome</Label>
              <Select value={completeData.outcome} onValueChange={v => setCompleteData(p => ({ ...p, outcome: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rescheduled">Rescheduled</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes / Discussion Summary</Label>
              <Textarea value={completeData.notes} onChange={e => setCompleteData(p => ({ ...p, notes: e.target.value }))} placeholder="What was discussed, decisions made, follow-up items..." className="text-sm min-h-[80px]" data-testid="review-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplete(false)}>Cancel</Button>
            <Button onClick={handleComplete} disabled={saving} className="bg-[#4A3B32] text-white hover:bg-[#3a2e26]" data-testid="confirm-complete-review">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
